import type {CustomPropertiesSchemaPagePayload, Repository} from '@github-ui/custom-properties-types'
import {listOrgReposPropertyValuesPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ReposFilter} from '@github-ui/repos-filter'
import {NoReposMessage, useListResults, type UserInfo} from '@github-ui/repos-list-shared'
import {useSearchParams} from '@github-ui/use-navigate'
import {useCallback, useMemo, useRef, useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {useSetFlash} from '../contexts/FlashContext'
import {EditRepoPropertiesDialog} from './EditRepoPropertiesDialog'
import {PropertiesEditingPage} from './PropertiesEditingPage'
import {PropertiesHeaderPageTabs} from './PropertiesHeaderPageTabs'
import {ReposPropertiesList} from './ReposPropertiesList'

// This page can only be accessed by org admins
const adminUserInfo: UserInfo = {
  admin: true,
  // This page never allows creating repos, no matter the current user
  canCreateRepository: false,
  directOrTeamMember: true,
}

export function SetValuesPage() {
  const payload = useRoutePayload<CustomPropertiesSchemaPagePayload>()
  const {definitions, pageCount, repositories, repositoryCount, permissions} = payload

  const [initialParams] = useSearchParams()
  const [editingRepos, setEditingRepos] = useState<Repository[]>([])
  const [query, setQuery] = useState(initialParams.get('q') || '')
  const org = useCurrentOrg()

  const setFlash = useSetFlash()

  const showFilterDialog = useFeatureFlag('repos_list_show_filter_dialog')
  const propertiesEditingPageEnabled = useFeatureFlag('custom_properties_editing_page')

  const {results, currentPage, isFetching, fetchResults} = useListResults<Repository>(
    {repositories, pageCount, repositoryCount},
    listOrgReposPropertyValuesPath({org: org.login}),
    Number(initialParams.get('page')),
  )

  const editableProperties = useMemo(() => definitions.map(d => d.propertyName), [definitions])

  const onEditSuccess = () => {
    fetchResults({page: currentPage, q: query})
    setFlash('repos.properties.updated')
  }

  const dismissDialog = () => setEditingRepos([])
  const selectRepos = useCallback(
    (repositorySelections: Repository[]) => {
      setFlash(null)
      setEditingRepos(repositorySelections)
    },
    [setFlash, setEditingRepos],
  )

  const clickedEditButtonRef = useRef<null | HTMLButtonElement>(null)

  const isEditing = !!editingRepos.length

  if (propertiesEditingPageEnabled && isEditing) {
    return (
      <PropertiesEditingPage
        definitions={definitions}
        editableProperties={editableProperties}
        onDismiss={() => {
          clickedEditButtonRef.current?.focus()
          dismissDialog()
        }}
        onSuccess={() => {
          onEditSuccess()
          dismissDialog()
        }}
        editingRepos={editingRepos}
      />
    )
  }

  return (
    <>
      <PropertiesHeaderPageTabs permissions={permissions} definitions={definitions} />
      <ReposFilter
        id="repos-list-filter"
        sx={{flex: 1, mb: 3}}
        label="Search repositories"
        variant={showFilterDialog ? 'full' : 'input'}
        placeholder="Search repositories"
        filterValue={query}
        showSpinner={isFetching}
        definitions={definitions}
        onChange={q => {
          setQuery(q)
          fetchResults({page: 1, q})
        }}
      />
      <div data-hpc>
        {results.repositories.length ? (
          <ReposPropertiesList
            repositoryCount={results.repositoryCount}
            pageCount={results.pageCount}
            repos={results.repositories}
            page={currentPage}
            query={query}
            onPageChange={page => fetchResults({page, q: query})}
            onEditRepoPropertiesClick={selectRepos}
            clickedEditButtonRef={clickedEditButtonRef}
          />
        ) : (
          <NoReposMessage
            currentPage={currentPage}
            pageCount={results.pageCount}
            filtered={!!query}
            userInfo={adminUserInfo}
          />
        )}
      </div>

      {isEditing && (
        <EditRepoPropertiesDialog
          definitions={definitions}
          editableProperties={editableProperties}
          onDismiss={() => {
            clickedEditButtonRef.current?.focus()
            dismissDialog()
          }}
          onSuccess={() => {
            onEditSuccess()
            dismissDialog()
          }}
          editingRepos={editingRepos}
        />
      )}
    </>
  )
}

try{ SetValuesPage.displayName ||= 'SetValuesPage' } catch {}